// @/libs/authService.js
import { apiClient } from '@/libs/apiClient';
import { cache } from 'react';
import { globalNavigate } from '@/libs/useNavigate';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User data
 */
export async function loginWithEmail(email: string, password: string) {
  try {
    const response = await apiClient.post('/users/login', {
      email,
      password,
      type: 'email',
    });
    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

/**
 * Login with social provider
 * @param {Object} params - Social login parameters
 * @param {string} params.idToken - ID token from social provider
 * @param {string} params.type - Provider type (google, microsoft)
 * @returns {Promise<Object>} User data
 */
export async function loginWithSocial({ idToken, type }: { idToken: string; type: string }) {
  try {
    const response = await apiClient.post('/users/login', {
      id_token: idToken,
      type: type || undefined,
    });
    return response.data;
  } catch (error) {
    console.error(`${type} login failed:`, error);
    throw error;
  }
}

/**
 * Handle Pn SSO callback
 * @param {string} token - JWT token from Pn FEP
 * @param {boolean} linkMode - If true, link GID to current user instead of login
 * @returns {Promise<Object>} Response with user data or registration redirect
 */
export async function loginWithPn(token: string, linkMode: boolean = false) {
  try {
    const params = new URLSearchParams({ token });
    if (linkMode) {
      params.set('link_mode', 'true');
    }
    const response = await apiClient.get(`/auth/pn/callback?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Pn SSO failed:', error);
    throw error;
  }
}

/**
 * Get Pn SSO configuration
 * @returns {Promise<Object>} Pn SSO config with fep_login_url and callback_url
 */
export async function getPnConfig() {
  try {
    const response = await apiClient.get('/auth/pn/config');
    return response.data;
  } catch (error) {
    console.error('Failed to get Pn config:', error);
    throw error;
  }
}

/**
 * Cached function to fetch open config data
 * Using React 19's cache API to prevent duplicate requests and infinite loops
 * @returns {Promise<Object>} Config data
 */
export const fetchOpenConfig = cache(async () => {
  try {
    const response = await apiClient.get('/config/open');
    return response;
  } catch (error) {
    console.error('Failed to fetch config:', error);
    throw error;
  }
});

/**
 * Logout the current user
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await apiClient.post('/users/logout');
    // Clear any auth tokens or user data from local storage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('org_id');
    globalNavigate('/login');
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
}
